import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Typography, Spin, Empty } from 'antd';
import dayjs from 'dayjs';
import { useOrders } from '@hooks';

const { Title, Paragraph } = Typography;

export const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const { getOrderBySessionId } = useOrders();
  const { data: order, isLoading, isError } = getOrderBySessionId(sessionId || '');

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-[#00ade6]/20 text-primary',
      paid: 'bg-green-500/20 text-green-500',
      processing: 'bg-blue-500/20 text-blue-500',
      shipped: 'bg-purple-500/20 text-purple-500',
      delivered: 'bg-green-600/20 text-green-600',
      cancelled: 'bg-red-500/20 text-red-500',
    };
    return statusColors[status.toLowerCase()] || 'bg-gray-500/20 text-gray-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <Paragraph className="font-android text-white text-xl mt-4">
            Loading your order...
          </Paragraph>
        </div>
      </div>
    );
  }

  if (isError || !order || !sessionId) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Empty
            description={
              <span className="font-android text-white text-xl">
                Unable to load order details
              </span>
            }
          />
          <Paragraph className="font-android text-zinc-400 mt-4 mb-6">
            Your order may still be processing. Please check your email for confirmation.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/merch')}
            className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-xl"
          >
            Return to Store
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Success Header */}
      <section className="bg-darker py-20 border-b-2 border-primary/20">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-8 animate-bounce">
            <CheckCircleOutlined 
              className="text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]" 
              style={{ fontSize: '100px' }} 
            />
          </div>
          <Title 
            level={1} 
            className="font-azonix !font-normal !text-6xl md:!text-7xl text-primary uppercase mb-6 drop-shadow-[0_0_30px_rgba(0,173,230,0.5)]"
          >
            PAYMENT SUCCESSFUL!
          </Title>
          <Paragraph className="font-coda text-white !text-2xl mb-4 font-normal">
            Thank you for your purchase!
          </Paragraph>
          <div className="inline-block bg-zinc-800 border-2 border-primary px-8 py-4 mt-4">
            <Paragraph className="font-coda text-white !text-lg mb-2 font-normal">
              ORDER NUMBER
            </Paragraph>
            <Paragraph className="font-coda text-primary !text-2xl font-normal mb-0">
              {order.orderNumber}
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Order Details */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-zinc-900 border-2 border-zinc-700 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#00ade6] to-[#0095c7] px-8 py-6">
              <Title level={2} className="font-azonix !font-normal !text-black uppercase flex items-center justify-center mb-0">
                ORDER DETAILS
              </Title>
            </div>

            {/* Order Information Grid */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-azonix text-primary text-sm uppercase mb-2 font-normal">
                    Order Number
                  </Paragraph>
                  <Paragraph className="font-coda text-white text-xl mb-0 font-normal">
                    {order.orderNumber}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-azonix text-primary text-sm uppercase mb-2 font-normal">
                    Date
                  </Paragraph>
                  <Paragraph className="font-coda text-white text-xl mb-0 font-normal">
                    {dayjs(order.createdAt).format('MMM DD, YYYY')}
                  </Paragraph>
                  <Paragraph className="font-coda text-white text-sm mb-0 font-normal">
                    {dayjs(order.createdAt).format('HH:mm')}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-azonix text-primary text-sm uppercase mb-2 font-normal">
                    Email
                  </Paragraph>
                  <Paragraph className="font-coda text-white text-lg mb-0 break-all font-normal">
                    {order.customerEmail}
                  </Paragraph>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mb-8">
                <Paragraph className="font-azonix text-primary text-sm uppercase mb-3 font-normal">
                  Status
                </Paragraph>
                <span 
                  className={`inline-block px-6 py-3 ${getStatusColor(order.status)} font-azonix font-normal text-white uppercase text-lg border-2 border-current`}
                >
                  {order.status}
                </span>
              </div>

              {/* Items Table */}
              <div className="mb-8">
                <Title level={3} className="font-azonix !font-normal text-primary uppercase mb-4 !text-2xl">
                  Items Ordered
                </Title>
                <div className="bg-zinc-800 border-2 border-zinc-700 overflow-hidden">
                  <div className="overflow-x-auto hide-scrollbar">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-4 bg-zinc-900 px-6 py-4 border-b-2 border-primary min-w-[600px]">
                      <div className="col-span-5 font-azonix text-white uppercase font-normal text-lg">
                        Product
                      </div>
                      <div className="col-span-2 font-azonix text-white uppercase font-normal text-lg text-center">
                        Quantity
                      </div>
                      <div className="col-span-2 font-azonix text-white uppercase font-normal text-lg text-right">
                        Price
                      </div>
                      <div className="col-span-3 font-azonix text-white uppercase font-normal text-lg text-right">
                        Subtotal
                      </div>
                    </div>

                    {/* Table Body */}
                    {order.items.map((item, index) => (
                      <div 
                        key={item.id}
                        className={`grid grid-cols-12 gap-4 px-6 py-5 min-w-[600px] ${
                          index < order.items.length - 1 ? 'border-b border-zinc-700' : ''
                        } hover:bg-zinc-750 transition-colors`}
                      >
                        <div className="col-span-5 font-coda text-white text-lg font-normal">
                          {item.productName}
                        </div>
                        <div className="col-span-2 font-coda text-white text-lg text-center font-normal">
                          {item.quantity}
                        </div>
                        <div className="col-span-2 font-coda text-primary font-normal text-lg text-right">
                          {formatPrice(item.price)}
                        </div>
                        <div className="col-span-3 font-coda text-primary font-normal text-lg text-right">
                          {formatPrice(item.subtotal)}
                        </div>
                      </div>
                    ))}

                    {/* Subtotal */}
                    <div className="border-t border-zinc-700 px-6 py-4 min-w-[600px]">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-9 font-azonix text-zinc-400 uppercase font-normal text-lg text-right">
                          Subtotal:
                        </div>
                        <div className="col-span-3 font-coda text-zinc-400 font-normal text-lg text-right">
                          {formatPrice(order.subtotalBeforeDiscount)}
                        </div>
                      </div>
                    </div>

                    {/* Discount (only show if there's a discount) */}
                    {order.discountAmount > 0 && (
                      <div className="px-6 py-4 bg-green-500/10 min-w-[600px]">
                        <div className="grid grid-cols-12 gap-4">
                          <div className="col-span-9 font-azonix text-green-500 uppercase font-normal text-lg text-right">
                            Tier Discount:
                          </div>
                          <div className="col-span-3 font-coda text-green-500 font-normal text-lg text-right">
                            -{formatPrice(order.discountAmount)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    <div className="bg-[#00ade6] px-6 py-5 min-w-[600px]">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-9 font-azonix text-white uppercase font-normal text-2xl text-right">
                          TOTAL:
                        </div>
                        <div className="col-span-3 font-coda text-white font-normal text-2xl text-right">
                          {formatPrice(order.totalAmount)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Next Section */}
              <div className="bg-gradient-to-r from-zinc-800 to-zinc-850 border-2 border-primary/30 p-6 mb-8">
                <Paragraph className="font-azonix text-primary text-xl mb-3 font-normal uppercase">
                  What's next?
                </Paragraph>
                <Paragraph className="font-coda text-white text-base leading-relaxed mb-0 font-normal">
                  You will receive a confirmation email at{' '}
                  <span className="text-primary font-coda font-normal">{order.customerEmail}</span>{' '}
                  with your order details and tracking information.
                </Paragraph>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate('/merch')}
                  className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-xl h-16 px-8"
                >
                  Continue Shopping
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate('/')}
                  className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-xl h-16 px-8"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

